import { takeEvery, call, put, select } from "redux-saga/effects";
import { FilterActionTypes, updateFilterResult, IApplySingleFacetFilterAction, IRemoveSingleFacetFilterAction, IApplySortingFilterAction, IUpdateFilterResultAction, IApplyFacetsAction, IApplySearchFilterAction, initFilter, IClearFilterAction, ILoadMoreFilterAction } from "./filter.actions";
import { fetchFilterData } from "@montana/services/filter.service";
import { IProductList, IMontanaFacet, IProfessionalList } from "@montana/models/generated/cms.interface";
import { IStoreState } from "@montana/store/store";
import { IFilterContainerState } from "./filter";
import { SortingOption, OmitConfig } from "@montana/models/dev";
import { isBrowser } from "@montana/helpers/browser.helper";
import { connectServiceFunctions } from "../base/base.sagas";

const SEARCH_PARAM = 'q';
const SORT_PARAM = 'sortby';
const PAGINATION_PARAM = 'p';

const qsBlackList = [
    "utm",
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "gclid",
    "gclsrc",
    "fbclid",
    "paid",
    "bannerid",
    "partnerid",
    "dclid",
    "mscklid",
    "zanpid"
];

export function* onInitFilter(filterId: number, filterLanguage: string, path: string, customerMode: string) {

    yield put(initFilter(filterId, filterLanguage));

    // Initialize the filter based on url params
    const search = isBrowser ? location.search : path.includes('?') ? path.split('?')[1] : '';

    // Extract all queryparams as pairs
    const filterParamsFromUrl: string[] = search
        .replace("?", "")
        .split("&")
        .filter(param => !!param);

    // Extract 3 specific params --> search, sorting and pagination
    const searchQuery = decodeURIComponent(filterParamsFromUrl.find(param => param.toLowerCase().startsWith(SEARCH_PARAM))
                                                              ?.split('=')[1] || '');

    const sorting = filterParamsFromUrl.find(param => param.toLowerCase().startsWith(SORT_PARAM))
                                       ?.split('=')[1] as SortingOption;

    // Get current page from URL params
    const pageParam = filterParamsFromUrl.find(param => param.toLowerCase().startsWith(`${PAGINATION_PARAM}=`))
                                         ?.split('=')[1];

    let page = Number(pageParam) || undefined;

    // Find handles pages like indexes (0-based), so we extract one from the more humanfriendly param if appropriate.
    if (page && page > 0) {
        page = page - 1;
    }

    // Facets are considered all params for the filterpage that does not match specific params, eg. search. For now...
    const facets = filterParamsFromUrl
        .filter(param => !param.startsWith(SEARCH_PARAM)
                         && !param.startsWith(SORT_PARAM)
                         && !param.startsWith(PAGINATION_PARAM)
                         && !qsBlackList.find(entry => param.startsWith(entry)))
        .map((param: string): IMontanaFacet => {
            const facetParam = param.split('=');

            return {
                facetKey: decodeURIComponent(facetParam[0]),
                facetValue: decodeURIComponent(facetParam[1]),
            }
        });

    return yield applyFilter(filterId, filterLanguage, searchQuery, facets, sorting, true, page, customerMode);
}

function* onLoadMore({ customerMode }: ILoadMoreFilterAction) {
    const { filterId, filterLanguage, searchQuery, appliedFacets, meta, sortBy }: IFilterContainerState = yield select((state: IStoreState) => state.filter);

    // If we are already on last page just stop.
    if (!meta || !Number.isInteger(meta?.currentPage) || meta.currentPage + 1 >= meta.totalPages) {
        return;
    }

    return yield applyFilter(filterId, filterLanguage, searchQuery, appliedFacets, sortBy, false, meta.currentPage + 1, customerMode);
}

function* onClearFilter({customerMode}: IClearFilterAction) {
    const { filterId, filterLanguage }: IFilterContainerState = yield select((state: IStoreState) => state.filter);
    return yield applyFilter(filterId, filterLanguage, '', [], undefined, undefined, undefined, customerMode);
}

function* onApplySorting({ sortBy }: IApplySortingFilterAction) {
    const { filterId, filterLanguage, searchQuery, appliedFacets }: IFilterContainerState = yield select((state: IStoreState) => state.filter);

    yield applyFilter(filterId, filterLanguage, searchQuery, appliedFacets, sortBy);
}

function* onApplySearch({ searchQuery }: IApplySearchFilterAction) {
    const { filterId, filterLanguage, appliedFacets, sortBy }: IFilterContainerState = yield select((state: IStoreState) => state.filter);

    yield applyFilter(filterId, filterLanguage, searchQuery, appliedFacets, sortBy);
}

function* onApplySingleFacet({ facet }: IApplySingleFacetFilterAction) {
    const { filterId, filterLanguage, searchQuery, appliedFacets, sortBy }: IFilterContainerState = yield select((state: IStoreState) => state.filter);
    const facetsToApply: IMontanaFacet[] = [...appliedFacets, facet];

    yield applyFilter(filterId, filterLanguage, searchQuery, facetsToApply, sortBy);
}

function* onRemoveSingleFacet({ facet }: IRemoveSingleFacetFilterAction) {
    const { filterId, filterLanguage, searchQuery, appliedFacets, sortBy }: IFilterContainerState = yield select((state: IStoreState) => state.filter);
    const facetsToApply: IMontanaFacet[] = [...appliedFacets.filter((appliedFacet: IMontanaFacet) => appliedFacet.facetKey !== facet.facetKey || appliedFacet.facetValue !== facet.facetValue)];

    yield applyFilter(filterId, filterLanguage, searchQuery, facetsToApply, sortBy);
}

function* onApplyFacets({ facets }: IApplyFacetsAction) {
    const { filterId, filterLanguage, searchQuery, sortBy }: IFilterContainerState = yield select((state: IStoreState) => state.filter);

    yield applyFilter(filterId, filterLanguage, searchQuery, facets, sortBy);
}

function* applyFilter(
    filterId: number,
    filterLanguage: string,
    searchQuery = '',
    facets: IMontanaFacet[] = [],
    sortBy: SortingOption = SortingOption.Manual,
    initialLoad = true,
    pageIndex = 0,
    customerMode = "private",
): any {
    const [cFetchFilterData]: [OmitConfig<typeof fetchFilterData>] = yield connectServiceFunctions(fetchFilterData);

    try {
        const response: Response = (yield call(cFetchFilterData, filterId, filterLanguage, searchQuery, facets, sortBy, initialLoad, pageIndex, customerMode))!;
        const { json, ok } = response;

        if (!ok) {
            throw new Error();
        }

        const productList: IProductList | IProfessionalList = (yield call(json.bind(response)))!;

        yield put(updateFilterResult(productList, searchQuery, facets, sortBy, initialLoad));
    } catch (e) {
        console.error(`Failed to fetch product list with filterid ${filterId} and language ${filterLanguage}`);
    }
}

function onMergeFilterParamIntoUrl(action: IUpdateFilterResultAction) {
    const { sortBy, searchQuery, appliedFacets, productList } = action;
    const { pathname, hash } = location;

    /**
     * Currently we run a clean slate for the search params,
     * meaning if a search param is not filter related it's not invited to the param-party
     * This might need to be altered, if other params needs to be taken into account,
     * however without knowing all facet names (which we do not at the time of writing) we cannot distinguish.
     */

    // Mapping to query params.
    const filterParams = appliedFacets.map(f => `${f.facetKey}=${f.facetValue}`);

    if (sortBy !== SortingOption.Manual) {
        filterParams.push(`${SORT_PARAM}=${sortBy}`);
    }

    if (searchQuery) {
        filterParams.push(`${SEARCH_PARAM}=${searchQuery}`);
    }

    if (productList?.meta?.currentPage && productList.meta.currentPage > 0) {
        filterParams.push(`${PAGINATION_PARAM}=${productList.meta.currentPage + 1}`);
    }

    const decodedSearch = decodeURIComponent(location.search);
    const oldParams = decodedSearch.substring(1).split("&").filter(p => !!p);

    const newParams = filterParams.filter(p => !decodedSearch.includes(p));
    const removeParams = oldParams.filter(p => !filterParams.find(fp => p === fp));

    if (!newParams.length && !removeParams.length) {
        return;
    }

    let updatedSearch = location.search;

    if (removeParams.length) {
        updatedSearch = removeParams.reduce((a, c) => a.replace("&"+c, "").replace("?"+c, ""), decodeURIComponent(updatedSearch));
    }

    if (newParams.length) {
        updatedSearch += (updatedSearch ? "&" : "?");
        updatedSearch += newParams.join("&");
    }

    history.replaceState(history.state, "", `${pathname}${hash}${updatedSearch}`);
}

export const filterSagas = [
    takeEvery(FilterActionTypes.LOAD_MORE, onLoadMore),
    takeEvery(FilterActionTypes.CLEAR_FILTER, onClearFilter),
    takeEvery(FilterActionTypes.APPLY_SINGLE_FACET, onApplySingleFacet),
    takeEvery(FilterActionTypes.APPLY_SEARCH, onApplySearch),
    takeEvery(FilterActionTypes.APPLY_SORTING, onApplySorting),
    takeEvery(FilterActionTypes.REMOVE_SINGLE_FACET, onRemoveSingleFacet),
    takeEvery(FilterActionTypes.APPLY_FACETS, onApplyFacets),
    takeEvery(FilterActionTypes.UPDATE_FILTER_RESULT, onMergeFilterParamIntoUrl),
];
